import React, { memo, useCallback, useMemo } from 'react'

import { classnames, Icon, Tooltip } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import { NOOP } from 'Utils/stableDefaults'

const iconSize = {
  height: '20px',
  width: '20px',
}

const DISABLED_REASON = 'Generate or upload a model to start editing it'

function ViewerSidebarItem({
  id,
  label = undefined,
  className = undefined,
  onClick: handleClick = NOOP,
  isDisabled = false,
  hideDisabledTooltip = false,
  disabledReason = DISABLED_REASON,
  icon,
  isStatic = false,
}) {
  const isActive = useThreeDStudioStore(
    state => !isStatic && state.ui?.activeViewerSidebarTab === id,
  )

  const iconComponent = useMemo(() => {
    return icon && (
      <Icon
        svgComponent={icon}
        color={isActive ? 'primary' : 'darker'}
        size={iconSize}
        notSpaced
      />
    )
  }, [icon, isActive])

  const handleItemClick = useCallback((event) => {
    if (isDisabled) {
      event?.preventDefault()
      return
    }

    handleClick(event)
  }, [handleClick, isDisabled])

  const handleKeyDown = useCallback((event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    handleItemClick(event)
  }, [handleItemClick])

  const showDisabledReason = isDisabled && !hideDisabledTooltip
  const ariaLabel = showDisabledReason && label ? `${label}. ${disabledReason}` : label

  const item = (
    <div
      role={isStatic ? undefined : 'button'}
      tabIndex={isStatic ? undefined : 0}
      aria-disabled={isStatic ? undefined : isDisabled}
      aria-label={isStatic ? undefined : ariaLabel}
      className={classnames(
        'viewer-sidebar__item',
        isActive && 'viewer-sidebar__item--active',
        isDisabled && 'viewer-sidebar__item--disabled',
        className,
      )}
      onClick={isStatic ? undefined : handleItemClick}
      onKeyDown={isStatic ? undefined : handleKeyDown}
    >
      <div className="viewer-sidebar__item-content">
        <span className="viewer-sidebar__item-icon">
          {iconComponent}
        </span>
        {label && (
          <span className="viewer-sidebar__item-label">
            {label}
          </span>
        )}
      </div>
    </div>
  )

  if (!showDisabledReason) {
    return item
  }

  return (
    <Tooltip
      content={disabledReason}
      id={`viewer-sidebar-${id}-disabled-tooltip`}
      place="left"
    >
      {item}
    </Tooltip>
  )
}

ViewerSidebarItem.propTypes = {
  id: string.isRequired,
  label: string,
  className: string,
  onClick: func,
  isDisabled: bool,
  hideDisabledTooltip: bool,
  disabledReason: string,
  icon: func.isRequired,
  isStatic: bool,
}

export default memo(ViewerSidebarItem)
